import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';
import {labels, currentResource} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';
import {useQuery} from '../../../util/customHooks';
import {useAresUrlParameters} from '../../../util/Ares/customHooks';
import {
	relateDocumentsTo,
	documentCategoryNames,
	relatedDocumentsTab,
	documentTypes,
	documentTabLayoutTabs,
	documentSearchTypes,
	pagingOptions
} from '../../../util/uiconstants';
import {deleteTaskDocumentRelate} from '../../../actions/RelateTask/relatetaskactions';

import Modal from '../Modal/Modal';
import DocumentName from '../DocumentName/DocumentName';
import {
	getAllDocumentsToRelate,
	createRelatedDocument,
	deleteRelatedDocument,
	createFormBodyReference
} from '../../../actions/RelatedDocuments/relatedDocumentsActions';
import {clearErrors} from '../../../actions/erroractions';
import {createTaskDocumentRelation} from '../../../actions/Task/taskdetaildocumentsaction';
import {setIsModalOpen} from '../../../actions/setismodalopen';
import _ from 'lodash';
import {updateDocument} from '../../../actions/Document/documentactions';
import RelateItemList from '../RelateList/RelateItemList';
import {
	relateDocumentToFlowChartStep,
	unrelateDocumentToFlowChartStep
} from '../../../actions/FlowChart/flowchartstepactions';
import EngagementSelector from '../EngagementSelector/EngagementSelector';

export default function RelateDocuments({
	activeTab,
	relatedDocumentsList,
	showModal,
	onClose,
	engagementId,
	documentId,
	entityUid,
	comingFrom,
	isOverwriteModal,
	knowledgeFormId,
	setShowModal,
	radioClick,
	documentTypeId,
	isAresView,
	documentCount,
	setDocumentCount,
	documentRelatedTaskIds = [],
	showEngagementSelectionDropdown = false,
	groupInstructionDrawer = false,
	callbackFunction,
	relatedDocumentsToInstruction,
	sectionId,
	instructionDocuments,
	signoffRequirements,
	dontShowEngagementSelector = false,
	excludeDocumentFilter = false,
	title,
	getRelatedDocuments,
	relateUnrelateFlowchartDocument = false,
	flowchartId,
	formBodyId,
	modalLevel,
	isModalOpen
}) {
	const [searchText, setSearchText] = useState(''),
		[page, setPage] = useState(1),
		[pageSize, setPageSize] = useState(pagingOptions.defaultPageSize),
		[showAddedOnly, setShowAddedOnly] = useState(false),
		[isDocumentSelected, setIsDocumentSelected] = useState(false),
		[showError, setShowError] = useState(false),
		[engagementValue, setEngagementValue] = useState(engagementId);

	const documents = useSelector((store) => store.documents);
	const engagement = useSelector((store) => store.engagement);
	const engagementMetaData = useSelector((store) => store.engagementMetaData);
	const tasksAssociatedToDocument = useSelector((state) => state.document?.tasks);
	const aresUrlParameters = useAresUrlParameters();

	// Map relatedDocuments array into a key-value pair, where the key is the id property of every element, and the value is the element itself
	let relatedDocumentIds = useRef({});

	if (groupInstructionDrawer) {
		relatedDocumentsList = relatedDocumentsToInstruction;
	}
	if (relatedDocumentsList !== undefined && !_.isEmpty(relatedDocumentsList && relatedDocumentsList.data)) {
		relatedDocumentIds.current = relatedDocumentsList.data.reduce((acc, item) => {
			acc[item.id] = true;
			return acc;
		}, {});
	}

	const categoryName = useMemo(() => {
		switch (activeTab) {
			case relatedDocumentsTab.EVIDENCE:
				return documentCategoryNames.evidence;
			case relatedDocumentsTab.TEMPORARY:
				return documentCategoryNames.temporary;
			case relatedDocumentsTab.PRIOR_PERIOD:
				return documentCategoryNames.priorPeriod;
			case relatedDocumentsTab.EXTERNAL_DOCUMENTS:
				return documentCategoryNames.external;
		}
	}, [activeTab]);

	const workspaceId = useMemo(() => engagement.workspaceId, [engagement]);

	const queryStringParameters = useQuery();

	const taskIds = useMemo(() => {
		const ids = [];

		const hasTaskId = Object.prototype.hasOwnProperty.call(queryStringParameters, 'taskid');

		if (hasTaskId) {
			ids.push(queryStringParameters.taskid);
		}

		if (documentRelatedTaskIds.length > 0) {
			ids.push(...documentRelatedTaskIds);
		}

		return [...new Set(ids)];
	}, [queryStringParameters, documentRelatedTaskIds]);

	const dispatch = useDispatch();

	const onCloseModal = () => {
		onClose && onClose();
	};

	const onEngagementChange = (opt) => setEngagementValue(opt.value);

	const engagementSelector = useCallback(
		() => (
			<>
				{showEngagementSelectionDropdown &&
					engagementMetaData?.totalpageditems &&
					engagementMetaData?.totalpageditems > 0 && (
						<EngagementSelector
							placeholder={labels.engagement}
							engagementMetadata={engagementMetaData.engagementmetadatadetails}
							onEngagementChange={onEngagementChange}
							sourceEngagementId={engagementId}
							showThisEngagement
						/>
					)}
			</>
		),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[engagementMetaData]
	);

	const fetchDocuments = useCallback(
		(_, __, ___, ____, _____) => {
			let promise = Promise.resolve();
			const thisEngagement = engagementId;

			let engagementIdToSearch = thisEngagement,
				includeOtherEngagementsInWorkspace = false;

			if (engagementValue === null) {
				// Workspace
				includeOtherEngagementsInWorkspace = true;
			} else if (engagementValue !== thisEngagement) {
				// other engagement
				engagementIdToSearch = engagementValue;
			}

			const getFilterEntityType = () => {
				if (includeOtherEngagementsInWorkspace && engagementValue === null) {
					return documentCategoryNames.workspace;
				}

				const isTemporaryOrExternal =
					activeTab === relatedDocumentsTab.TEMPORARY || activeTab === relatedDocumentsTab.EXTERNAL_DOCUMENTS;
				const isAccountStandardWorkingPaper = documentTypeId === documentTypes.ACCOUNT_STANDARD_WORKING_PAPER;
				const isAccountStandardWorkingPaperTemporary =
					documentTypeId === documentTypes.ACCOUNT_STANDARD_WORKING_PAPER_TEMPORARY;

				if (isTemporaryOrExternal) {
					if (isAccountStandardWorkingPaper || isAccountStandardWorkingPaperTemporary) {
						return documentSearchTypes.account;
					}
					return documentSearchTypes.engagement;
				}
				return documentSearchTypes.engagement;
			};

			const excludedDocumentTypes = documentTypes.INDEPENDENCEFORMTEMPLATE;
			if (showAddedOnly) {
				promise =
					typeof getRelatedDocuments === 'function'
						? getRelatedDocuments(searchText, page, pageSize)
						: Promise.resolve(getRelatedDocuments);
			} else {
				promise = dispatch(
					getAllDocumentsToRelate(
						engagementIdToSearch,
						pageSize,
						page,
						searchText,
						isOverwriteModal,
						knowledgeFormId,
						documentTypeId,
						documentId,
						getDocumentFilterType(),
						getFilterEntityType(),
						excludedDocumentTypes,
						groupInstructionDrawer,
						groupInstructionDrawer,
						excludeDocumentFilter
					)
				);
			}

			promise = promise
				.then((result) => {
					let resultWithoutCurrentDocument = {...result};
					if (result?.data) {
						resultWithoutCurrentDocument.data = result.data.filter((item) => item.id !== documentId);
						resultWithoutCurrentDocument.data = resultWithoutCurrentDocument.data.map((item) => ({
							...item
						}));
					}
					return resultWithoutCurrentDocument;
				})
				.finally(() => {});
			return promise;
		},
		[activeTab, engagementId, engagementValue, page, pageSize, searchText, showAddedOnly]
	);

	const getDocumentFilterType = () => {
		if (engagementId !== engagementValue) {
			return documentCategoryNames.sharedEvidence;
		}

		switch (activeTab) {
			case relatedDocumentsTab.EVIDENCE:
				return documentCategoryNames.evidence;
			case relatedDocumentsTab.PRIOR_PERIOD:
				return documentCategoryNames.priorPeriod;
			case relatedDocumentsTab.TEMPORARY:
				return documentCategoryNames.temporary;
			case relatedDocumentsTab.EXTERNAL_DOCUMENTS:
				return documentCategoryNames.external;
			default:
				return documentCategoryNames.evidence;
		}
	};

	const onCheckChanged = (item, checked) => {
		let promise = Promise.resolve();
		const body = {
			RelatedDocumentId: item.id
		};
		if (!groupInstructionDrawer) {
			if (checked) {
				const documentsArray = Array.isArray(documents?.data)
					? documents.data
					: Array.isArray(documents?.allIds)
					? documents.allIds.map((id) => documents.byId[id])
					: [];
				const documentName = documentsArray.find((documentItem) => documentItem.id === item.id)?.documentName || '';
				const isTemporaryOrExternal =
					activeTab === relatedDocumentsTab.TEMPORARY || activeTab === relatedDocumentsTab.EXTERNAL_DOCUMENTS;

				const createRelatedDoc = () => {
					if (!isTemporaryOrExternal) {
						return dispatch(createRelatedDocument(engagementId, documentId, formBodyId, body, comingFrom));
					}
				};

				const createTaskDocRelation = () => {
					return dispatch(createTaskDocumentRelation(engagementId, item.id, documentName, taskIds)).then(() => {
						if (!isTemporaryOrExternal) {
							return dispatch(createRelatedDocument(engagementId, documentId, entityUid, body, comingFrom)).then(() => {
								if (signoffRequirements) {
									return dispatch(
										updateDocument(
											engagementId,
											item.id,
											signoffRequirements,
											undefined,
											undefined,
											undefined,
											entityUid,
											'FORM_BODY_QUICK_REFERENCE'
										)
									);
								}
							});
						}
					});
				};

				switch (comingFrom) {
					case relateDocumentsTo.canvasFormBodyKronos:
					case relateDocumentsTo.canvasFormBodyAres:
						if (relateUnrelateFlowchartDocument) {
							promise = dispatch(relateDocumentToFlowChartStep(engagementId, flowchartId, entityUid, body)).then(
								createRelatedDoc
							);
						} else {
							promise = createTaskDocRelation();
						}
						break;
					default:
						promise = dispatch(createRelatedDocument(engagementId, documentId, entityUid, body, comingFrom));
						setDocumentCount && setDocumentCount(documentCount + 1);
				}
			}
			if (!checked) {
				const handleUnrelateDocument = () => {
					if (relateUnrelateFlowchartDocument) {
						return dispatch(unrelateDocumentToFlowChartStep(engagementId, flowchartId, entityUid, item.id)).then(() => {
							dispatch(deleteRelatedDocument(engagementId, documentId, formBodyId, item.id, comingFrom));
						});
					}
					const isTemporaryOrExternal =
						activeTab === relatedDocumentsTab.TEMPORARY || activeTab === relatedDocumentsTab.EXTERNAL_DOCUMENTS;

					let promise;
					if (isTemporaryOrExternal) {
						promise = dispatch(deleteTaskDocumentRelate(engagementId, aresUrlParameters.taskId, item.id, true));
					} else {
						promise = dispatch(deleteRelatedDocument(engagementId, documentId, entityUid, item.id, comingFrom));
					}
					if (setDocumentCount) {
						setDocumentCount(documentCount - 1);
					}
					return promise;
				};

				promise = handleUnrelateDocument();
			}
		}

		if (!groupInstructionDrawer) {
			return promise
				.then(() => {
					if (checked) {
						relatedDocumentIds.current[item.id] = true;
					} else {
						delete relatedDocumentIds.current[item.id];
					}
					getRelatedDocuments && getRelatedDocuments();
				})
				.catch(() => {
					item.showLabel = false;
				});
		} else {
			const getTaskIds = () => {
				const ids = [];
				if (aresUrlParameters.taskId) {
					ids.push(aresUrlParameters.taskId);
				} else {
					if (tasksAssociatedToDocument && tasksAssociatedToDocument.length > 0) {
						tasksAssociatedToDocument.map((task) => {
							ids.push(task.taskId);
						});
					}
				}
				return ids.length > 0 ? ids : null;
			};
			let document = {id: item.id, checked};
			promise = Promise.resolve(callbackFunction(document)).then(() => {
				if (checked) {
					relatedDocumentIds.current[item.id] = true;
					dispatch(createTaskDocumentRelation(engagementId, item.id, item.documentName, getTaskIds())).then(() => {
						return dispatch(
							createFormBodyReference(
								engagementId,
								documentId,
								entityUid,
								item.id,
								documentTabLayoutTabs.evidenceName,
								sectionId
							)
						);
					});
				} else {
					delete relatedDocumentIds.current[item.id];
					//It will remove the document of the task if it has RelatedInstructionsCount === 1 (TODO:add one as constant)
					//it needs to check if the document is not in document list or if it is in the list but it has only one related instruction
					if (instructionDocuments.some((x) => x.documentId === item.id && x.relatedInstructionsCount === 1)) {
						dispatch(deleteRelatedDocument(engagementId, documentId, entityUid, item.id, comingFrom));
					}
				}
			});
			return promise;
		}
	};

	const onSearch = (value) => {
		setSearchText(value);
		setPage(1);
	};

	const overWriteForm = () => {
		if (isDocumentSelected) {
			setShowModal(true);
		} else {
			setShowError(true);
		}
	};

	const isDocumentRelated = useCallback((item) => {
		return !!relatedDocumentIds.current[item.id];
	}, []);

	useEffect(() => {
		if (showModal) {
			dispatch(clearErrors()).then(function() {
				dispatch(setIsModalOpen(true));
			});
		}
	}, []);

	const itemComponent = useCallback((document) => {
		const isDisabled = engagementValue !== engagementId && document.confidentialityTypeId ? true : false;
		return (
			<>
				<DocumentName
					showShared
					size="small"
					confidentialityTypeId={document.confidentialityTypeId}
					engagementId={document.engagementId}
					documentId={document.id}
					documentName={document.documentName}
					documentTypeId={document.documentTypeId}
					fileExt={document.fileExt}
					fileSizeKB={document.fileSizeKB}
					hasComments={document.hasComments}
					hasHiddenSheets={document.hasHiddenSheets}
					hasHighlights={document.hasHighlights}
					isConflicted={document.isConflicted}
					trackChange={document.trackChange}
					isShared={document.isShared}
					isReturned={document.isReturned}
					isReadOnly={document.isReadOnly}
					lastModifiedFullName={document.lastModifiedFullName + ' '}
					modifiedDate={document.modifiedDate}
					enableOpenDocument={
						!(
							isOverwriteModal ||
							comingFrom === relateDocumentsTo.canvasFormBody ||
							comingFrom === relateDocumentsTo.canvasFormBodyAres
						)
					}
					isEPSignoffRequired={document.isEPSignoffRequired}
					isEPSignoffUserRequired={document.isEPSignoffUserRequired}
					isEQRSignoffRequired={document.isEQRSignoffRequired}
					isEQRSignoffUserRequired={document.isEQRSignoffUserRequired}
					smartEvidenceSourceId={document.smartEvidenceSourceId}
					disabled={isDisabled}
				/>
			</>
		);
	}, []);

	const onFilter = (selected, state) => {
		setPage(1);
		setShowAddedOnly(state.searchSelected);
	};

	const headerRender = () => (
		<StyledListHeader>
			<section className="instructional-text">
				{title || Utility.getInstructionalText(comingFrom, isOverwriteModal, categoryName)}
			</section>
		</StyledListHeader>
	);

	const configuration = useMemo(
		() => ({
			noResultsLabel: labels.noResultsFound,
			data: fetchDocuments,
			render: itemComponent,
			selected: isDocumentRelated,
			onCheckBoxChange: onCheckChanged
		}),
		[fetchDocuments, itemComponent]
	);

	const relateListHeaders = useCallback(
		(totalEntityCount) => (
			<RelateDocumentsWrapper className="RelateDocumentsWrapper">
				<section className="listHeader">
					<section className="listHeader_docName">{labels.documentName}</section>
					<section className="relateDocuments__counter">
						{totalEntityCount > 0 && (
							<section>
								{`${totalEntityCount > 1 ? labels.documentsCount : labels.documentCount}`.replace(
									'{0}',
									totalEntityCount
								)}
							</section>
						)}
					</section>
				</section>
			</RelateDocumentsWrapper>
		),
		[]
	);

	return (
		<RelateDocumentsWrapper className="RelateDocumentsWrapper">
			{showModal && (
				<Modal
					show={showModal}
					showButtons={false}
					showFooter={false}
					modalsize={'medium'}
					title={Utility.getModalTitle(comingFrom, isOverwriteModal, categoryName)}
					closeTitle={labels.closeLabel}
					onHide={onCloseModal}
					confirmBtnTitle={labels.overwriteLabel}
					closeBtnTitle={labels.cancelLabel}
					onOkClick={overWriteForm}
					modalContainerClass={isOverwriteModal ? 'OverwriteModalContainer' : ''}
					modalbodystyle={isOverwriteModal ? 'OverwriteModalBody' : ''}
					className={`${isAresView ? 'aresViewRelateDocumentsWrapper' : ''}`}
					isAresView={isAresView}
					modalLevel={modalLevel}
				>
					<RelateItemList
						search
						listHeaders={relateListHeaders}
						additionalToolbarComponent={activeTab === relatedDocumentsTab.TEMPORARY ? '' : engagementSelector()}
						componentHeader={headerRender}
						onSearch={onSearch}
						onFilter={onFilter}
						pagination
						searchPlaceholder={labels.search}
						searchClearHoverText={labels.clearHoverText}
						noResultsLabel={labels.noResultFound}
						paginationIconHover={currentResource.pagingSvgHoverText}
						paginationShowText={labels.pagingShowtext}
						configuration={configuration}
						showAddedLabel
						addedLabel={labels.added}
						page={page}
						showSelectedOnlyText={labels.showOnlyRelatedDocuments}
						pageSize={pageSize}
						onPageChanged={(page) => {
							setPage(page);
						}}
						onPageSizeChanged={(pageSize, searchString) => {
							setPageSize(pageSize);
							setPage(1);
						}}
						isModalOpen={isModalOpen}
					/>
				</Modal>
			)}
		</RelateDocumentsWrapper>
	);
}

const RelateDocumentsWrapper = styled.section`
	.HeaderWrapper {
		margin-bottom: 0;
		.instructionalText {
			margin-bottom: var(--px-10);
		}
	}

	.relateDocuments__checkbox {
		position: absolute;
		right: var(--px-20);
		top: var(--px-15);
		width: auto;
		max-width: var(--px-300);

		.motif-checkbox {
			.motif-checkbox-label {
				width: 90%;
			}
		}
	}

	.listHeader {
		display: flex;
		align-items: center;
		font-weight: bold;
		padding: 0 var(--px-20);

		.listHeader_docName {
			display: inline-flex;
			padding-left: var(--px-45);
		}

		.relateDocuments__counter {
			display: inline-flex;
			margin-left: auto;
		}
	}

	.DocumentNameWrapper.small {
		padding: 0 var(--px-5);
	}

	.motif-modal-body {
		height: inherit;
	}
`;

const StyledListHeader = styled.section`
	padding-top: var(--px-20);
	padding-bottom: 0;
	width: 100%;
	display: flex;
	font-size: var(--px-14);
	line-height: normal;
	flex-direction: column;
	.instructional-text {
		font-size: var(--px-15);
		line-height: normal;
		padding-bottom: var(--px-20);
	}
`;
